import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@material-ui/icons/Check';
import ClickableDiv from 'components/ClickableDiv';

const StepCard = ({
  stepNumber,
  caption,
  currentStep,
  canGoNext,
  gotoStep,
  maxStepForward,
  nextButtonIds
}) => {
  const validNext = useMemo(() => stepNumber === currentStep + 1 && canGoNext, [
    stepNumber,
    currentStep,
    canGoNext
  ]);

  const isCurrentStep = useMemo(() => currentStep === stepNumber, [
    currentStep,
    stepNumber
  ]);

  const isNextAvailable = useMemo(() => currentStep > stepNumber || validNext, [
    currentStep,
    stepNumber,
    validNext
  ]);

  const bgColor = isCurrentStep
    ? 'bg-info-dark-blue text-white'
    : 'bg-white text-gray-500';
  const borderColor =
    currentStep >= stepNumber ? 'border-blue-600' : 'border-neutral-60';

  const handleClick = useCallback(() => {
    if (maxStepForward <= 0) {
      return gotoStep(stepNumber);
    }

    if (stepNumber < currentStep) {
      return gotoStep(stepNumber);
    }

    if (stepNumber - currentStep <= maxStepForward) {
      if (nextButtonIds.length > 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (const id of nextButtonIds) {
          /**
           * This getElementById is a anti-pattern but given the complexity of the
           * forms and steps, this is the most reliable way to ensure we are
           * triggering the right "Next" button click to move to the next step.
           * We can revisit this in the future if we refactor the entire
           * form/step logic.
           */
          const button = document.getElementById(id);
          if (!button) {
            // If button is not found, continue to the next ID
            // eslint-disable-next-line no-continue
            continue;
          }

          button.click(); // Simulates a click on next the button
          break;
        }
      } else {
        return gotoStep(stepNumber);
      }
    }

    // equals current step, do nothing
    return undefined;
  }, [gotoStep, stepNumber, currentStep, maxStepForward, nextButtonIds]);

  return (
    <ClickableDiv
      className="flex"
      data-testid={`step-card-${stepNumber}`}
      onClick={isNextAvailable ? handleClick : null}
    >
      <div
        data-testid="progress-bar-number"
        className={`w-6 h-6 p-2 text-sm font-normal border leading-none rounded-3xl ${bgColor} ${borderColor} mr-2 flex items-center justify-center`}
      >
        {stepNumber >= currentStep ? (
          <span
            className={`${currentStep < stepNumber ? 'text-neutral-60' : ''}`}
          >
            {stepNumber}
          </span>
        ) : (
          <CheckIcon style={{ fontSize: 14, color: '#0071CD' }} />
        )}
      </div>
      <div
        data-testid="progress-bar-text"
        className={`flex items-center w-full whitespace-no-wrap  ${isNextAvailable &&
          'hover:text-info-dark-blue text-neutral-600 cursor-pointer'} ${
          isCurrentStep ? 'text-sm font-semibold' : 'text-neutral-60'
        }`}
      >
        {caption}
      </div>
    </ClickableDiv>
  );
};

StepCard.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  caption: PropTypes.string.isRequired,
  currentStep: PropTypes.number.isRequired,
  canGoNext: PropTypes.bool.isRequired,
  gotoStep: PropTypes.func.isRequired,
  maxStepForward: PropTypes.number.isRequired,
  nextButtonIds: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default StepCard;
